// Import required libraries
import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

// STLViewer component receives the file URL and optional size
const STLViewer = ({ fileUrl, size = "small" }) => {
  const mountRef = useRef(null); // Ref to attach the renderer DOM element

  useEffect(() => {
    if (!fileUrl || !mountRef.current) return; // Exit early if no file or ref

    const mount = mountRef.current; // Cache the mount element

    // Create the scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(size === "small" ? "#000000" : "#00a1d6"); // Background color based on size

    // Setup camera with different positions based on size
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    if (size === "small") {
      camera.position.set(0, 0, 45); // For thumbnails or previews
    } else {
      camera.position.set(0, 70, 3); // For full view
    }

    // Setup WebGL renderer
    const renderer = new THREE.WebGLRenderer({ alpha: true }); // Allow transparent background
    const width = size === "small" ? 160 : 1000;
    const height = size === "small" ? 150 : 750;
    renderer.setSize(width, height);
    renderer.shadowMap.enabled = true; // Enable shadows
    mount.appendChild(renderer.domElement); // Attach renderer to DOM

    // Add orbit controls to allow user interaction
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true; // Smooth camera movement
    controls.dampingFactor = 0.05;
    controls.rotateSpeed = 1;
    controls.zoomSpeed = 1.2;
    controls.enablePan = true;
    controls.enableRotate = true;
    controls.enableZoom = true;
    controls.minPolarAngle = 0;
    controls.maxPolarAngle = Math.PI * 2;
    controls.autoRotate = false;

    if (size === "small") {
      controls.enabled = false; // Disable controls for thumbnails
    }

    // Load STL model
    const loader = new STLLoader();
    loader.load(fileUrl, (geometry) => {
      // Setup mesh with material
      const material = new THREE.MeshStandardMaterial({
        color: 0xeeeeee,
        metalness: 0,
        roughness: 1,
        envMapIntensity: 2,
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      scene.add(mesh);

      // Center the mesh in the scene
      const boundingBox = new THREE.Box3().setFromObject(mesh);
      const center = new THREE.Vector3();
      boundingBox.getCenter(center);
      mesh.position.sub(center); // Offset mesh to center it

      // Point the camera to the center of the mesh
      camera.lookAt(center);

      // Add lighting to the scene

      // Ambient light provides general lighting
      scene.add(new THREE.AmbientLight(0xffffff, 1.2));

      // Directional lights for realistic shading
      const dirLight1 = new THREE.DirectionalLight(0xffffff, 1);
      dirLight1.position.set(5, 10, 5);
      dirLight1.castShadow = true;
      scene.add(dirLight1);

      const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.8);
      dirLight2.position.set(-5, -10, 5);
      dirLight2.castShadow = true;
      scene.add(dirLight2);

      // Additional point light
      const pointLight = new THREE.PointLight(0xffffff, 1.5, 100);
      pointLight.position.set(0, 5, 10);
      pointLight.castShadow = true;
      scene.add(pointLight);

      // Hemisphere light for ambient environment lighting
      const hemiLight = new THREE.HemisphereLight(0xaaaaaa, 0x444444, 1);
      scene.add(hemiLight);

      // Animation loop
      const animate = () => {
        requestAnimationFrame(animate);
        controls.update(); // Required for damping and interaction
        renderer.render(scene, camera); // Render the scene
      };
      animate(); // Start animation loop
    });

    // Cleanup when component unmounts
    return () => {
      if (mount && renderer.domElement) {
        mount.removeChild(renderer.domElement); // Remove canvas from DOM
      }
      renderer.dispose(); // Free GPU resources
    };
  }, [fileUrl, size]); // Rerun effect when fileUrl or size changes

  // JSX element that will host the 3D canvas
  return <div ref={mountRef} />;
};

export default STLViewer;
